import React from "react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { WelcomeScreenWidgetProps, WelcomeScreenWidgetState } from "./types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { FONT_WEIGHT_OPTIONS_KEYWORDS } from "../../../../utils/fontWeightOptions";
import { themeColor } from "../../../../types/color";

const defaultState: WelcomeScreenWidgetState = {
  // Icon container
  iconContainerSize: 80,
  iconContainerBgColor: themeColor("background"),
  iconContainerBorderRadius: 50,

  // Icon
  iconSize: 40,
  iconColor: themeColor("textPrimary"),

  // Title
  titleFontSize: 28,
  titleFontWeight: "semibold",
  titleColor: themeColor("textPrimary"),
  titleMarginBottom: 12,

  // Description
  descriptionFontSize: 16,
  descriptionColor: themeColor("textPrimary"),
  descriptionMaxWidth: 500,
  descriptionLineHeight: 1.5,
  descriptionMarginBottom: 32,

  // Suggestions section title
  suggestionsTitleFontSize: 16,
  suggestionsTitleFontWeight: "medium",
  suggestionsTitleColor: themeColor("textPrimary"),
  suggestionsTitleMarginBottom: 16,

  // Suggestion pills
  suggestionPillPaddingX: 16,
  suggestionPillPaddingY: 10,
  suggestionPillBorderRadius: 20,
  suggestionPillBorderColor: themeColor("background"),
  suggestionPillBgColor: themeColor("transparent"),
  suggestionPillBgColorHover: themeColor("background"),
  suggestionPillTextColor: themeColor("textPrimary"),
  suggestionPillFontSize: 14,
  suggestionPillGap: 8,

  // Container
  containerPadding: 20,
};

const editFields: EditField[] = [
  // Icon container
  {
    key: "iconContainerSize",
    label: "Icon Container Size",
    type: "number",
    min: 40,
    max: 120,
    step: 8,
  },
  {
    key: "iconContainerBgColor",
    label: "Icon Container Background",
    type: "color",
  },
  {
    key: "iconContainerBorderRadius",
    label: "Icon Container Radius (%)",
    type: "number",
    min: 0,
    max: 50,
    step: 5,
  },

  // Icon
  {
    key: "iconSize",
    label: "Icon Size",
    type: "number",
    min: 20,
    max: 80,
    step: 4,
  },
  { key: "iconColor", label: "Icon Color", type: "color" },

  // Title
  {
    key: "titleFontSize",
    label: "Title Font Size",
    type: "number",
    min: 20,
    max: 40,
    step: 2,
  },
  {
    key: "titleFontWeight",
    label: "Title Font Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  { key: "titleColor", label: "Title Color", type: "color" },
  {
    key: "titleMarginBottom",
    label: "Title Margin Bottom",
    type: "number",
    min: 0,
    max: 32,
    step: 4,
  },

  // Description
  {
    key: "descriptionFontSize",
    label: "Description Font Size",
    type: "number",
    min: 12,
    max: 24,
    step: 1,
  },
  { key: "descriptionColor", label: "Description Color", type: "color" },
  {
    key: "descriptionMaxWidth",
    label: "Description Max Width",
    type: "number",
    min: 300,
    max: 700,
    step: 50,
  },
  {
    key: "descriptionLineHeight",
    label: "Description Line Height",
    type: "number",
    min: 1,
    max: 2,
    step: 0.1,
  },
  {
    key: "descriptionMarginBottom",
    label: "Description Margin Bottom",
    type: "number",
    min: 16,
    max: 64,
    step: 4,
  },

  // Suggestions section title
  {
    key: "suggestionsTitleFontSize",
    label: "Suggestions Title Size",
    type: "number",
    min: 12,
    max: 24,
    step: 1,
  },
  {
    key: "suggestionsTitleFontWeight",
    label: "Suggestions Title Weight",
    type: "options",
    options: [...FONT_WEIGHT_OPTIONS_KEYWORDS],
  },
  {
    key: "suggestionsTitleColor",
    label: "Suggestions Title Color",
    type: "color",
  },
  {
    key: "suggestionsTitleMarginBottom",
    label: "Suggestions Title Margin",
    type: "number",
    min: 8,
    max: 32,
    step: 4,
  },

  // Suggestion pills
  {
    key: "suggestionPillPaddingX",
    label: "Pill Padding X",
    type: "number",
    min: 8,
    max: 24,
    step: 2,
  },
  {
    key: "suggestionPillPaddingY",
    label: "Pill Padding Y",
    type: "number",
    min: 6,
    max: 16,
    step: 2,
  },
  {
    key: "suggestionPillBorderRadius",
    label: "Pill Border Radius",
    type: "number",
    min: 0,
    max: 30,
    step: 2,
  },
  {
    key: "suggestionPillBorderColor",
    label: "Pill Border Color",
    type: "color",
  },
  { key: "suggestionPillBgColor", label: "Pill Background", type: "color" },
  {
    key: "suggestionPillBgColorHover",
    label: "Pill Background (Hover)",
    type: "color",
  },
  { key: "suggestionPillTextColor", label: "Pill Text Color", type: "color" },
  {
    key: "suggestionPillFontSize",
    label: "Pill Font Size",
    type: "number",
    min: 12,
    max: 18,
    step: 1,
  },
  {
    key: "suggestionPillGap",
    label: "Pill Gap",
    type: "number",
    min: 4,
    max: 16,
    step: 2,
  },

  // Container
  {
    key: "containerPadding",
    label: "Container Padding",
    type: "number",
    min: 0,
    max: 40,
    step: 4,
  },
];

export const WelcomeScreenWidget: React.FC<WelcomeScreenWidgetProps> = ({
  initialState = {},
  widgetId,
  displayName,
  children,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    resolveColor,
    canSave,
  } = useWidgetEditable<WelcomeScreenWidgetState>(
    widgetId,
    { ...defaultState, ...initialState },
    editFields,
  );

  // Show loading state while fetching theme config
  if (isLoading) {
    return <></>;
  }

  // Render the children with current state
  const widgetContent = children(state, resolveColor);

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
